'use strict';

var toastMessageHelper = require('../../utils/toastMessageHelper');

// Debounce timer to avoid too many API calls
var pincodeTimer = null;

/* ===============================
   PROGRESS BAR + STAGES (EXISTING)
================================ */

var MAX_CHILDREN = 3;
var MAX_DISCONTINUATIONS = 6;

/**
 * Updates the multi-step progress indicator UI.
 * Marks previous steps as completed and the current step as active.
 *
 * @param {number} step - 1-based step number to set as active.
 * @returns {void}
 */
function updateProgressBar(step) {
    $('.stage-label').removeClass('active completed');
    $('.state-divider').removeClass('completed');

    for (var i = 1; i < step; i++) {
        $('.stage-label[data-step="' + i + '"]').addClass('completed');
        $('.state-divider[data-divider="' + i + '"]').addClass('completed');
    }

    $('.stage-label[data-step="' + step + '"]').addClass('active');
}

/**
 * Fetches city and state information for a given Indian pincode using the Google Geocoding API,
 * then populates the corresponding loyalty address form fields.
 *
 * @param {string|number} pincode - The postal code used to resolve location details.
 * @returns {void} Does not return a value; updates DOM fields asynchronously via AJAX callback.
 *
 * @description
 * - Reads the Google Maps API key from `#google-maps-api-key`.
 * - Calls the Geocoding endpoint with `<pincode>,India`.
 * - Clears `#loyaltyAddressCity` and `#loyaltyAddressState` before processing the response.
 * - Extracts:
 *   - `locality` as city
 *   - `administrative_area_level_1` as state
 * - Sets matched values into `#loyaltyAddressCity` and `#loyaltyAddressState`,
 *   triggering `input` events after assignment.
 */
function fetchPlace(pincode) {
    var googleApiKey = $('#google-maps-api-key').val();
    var apiUrl = 'https://maps.googleapis.com/maps/api/geocode/json?address=' + pincode + ',India&key=' + googleApiKey;

    $.getJSON(apiUrl, function (data) {
        $('#loyaltyAddressCity').val('');
        $('#loyaltyAddressState').val('');

        if (data.status === 'OK' && data.results.length) {
            var result = data.results[0];
            var city = '';
            var state = '';

            result.address_components.forEach(function (component) {
                if (component.types.includes('locality')) {
                    city = component.long_name;
                }
                if (component.types.includes('administrative_area_level_1')) {
                    state = component.long_name;
                }
            });

            if (city) $('#loyaltyAddressCity').val(city).trigger('input');
            if (state) $('#loyaltyAddressState').val(state).trigger('input');
        }
    });
}

/**
 * Validates (and lightly normalizes) a single form field, updating its UI error state.
 *
 * Behavior depends on the field type / marker classes:
 * - Name fields (`.first-name-field`, `.last-name-field`):
 *   - Removes non-letters (keeps spaces), trims leading spaces, collapses multiple spaces.
 *   - Valid pattern: letters with optional single spaces between words.
 *   - If required and empty → missing error.
 * - Email fields (`type="email"`):
 *   - Trims value and validates a basic `local@domain.tld` structure.
 * - Phone fields (`type="tel"`):
 *   - Strips non-digits.
 *   - Uses the field `maxlength` (default 10) as the minimum length requirement.
 *   - If `data-dialing-code` is `+91`, enforces first digit 6–9 (otherwise clears the value).
 * - Pincode fields (`.pin-code-field`):
 *   - Digits-only, max 6 digits.
 *   - If < 6 digits → clears `#loyaltyAddressCity` and `#loyaltyAddressState`.
 *   - If exactly 6 digits → debounced call to `fetchPlace(pincode)`.
 * - Other fields:
 *   - Trims and checks required.
 *
 * UI/error handling:
 * - For required fields: toggles `is-invalid` on the closest `.form-group` and writes the
 *   message into `.invalid-feedback` (preferring `data-missing-error` / `data-invalid-error`).
 * - For optional loyalty mobiles (`#loyaltyResidentialMobile`, `#loyaltyOfficeMobile`):
 *   when non-empty, enforces the same length rule as phone fields.
 * - For optional fax (`#loyaltyFaxNumber`): when non-empty, enforces `minlength`/`maxlength`.
 *
 * @param {JQuery} $field Input/select/textarea wrapped in jQuery.
 * @returns {boolean} `true` when the field is considered valid; otherwise `false`.
 */
function validateField($field) {
    var isValid = true;
    var errorMsg = '';
    var value;
    var maxlength;
    var cleanedValue;

    var isNameField = $field.hasClass('first-name-field') || $field.hasClass('last-name-field');
    var isEmailField = $field.attr('type') === 'email';
    var isPhoneField = $field.attr('type') === 'tel';
    var isPinCodeField = $field.hasClass('pin-code-field');
    var isOptionalLoyaltyField = $field.attr('id') === 'loyaltyResidentialMobile' || $field.attr('id') === 'loyaltyOfficeMobile';
    var isOptionalFaxField = $field.attr('id') === 'loyaltyFaxNumber';
    var isRequired = $field.hasClass('required') || $field.prop('required');

    var $formGroup = $field.closest('.form-group');
    var $errorDiv = $formGroup.find('.invalid-feedback');

    // -----------------------
    // Name validation
    // -----------------------
    if (isNameField) {
        value = $field.val() || '';

        // Remove digits and special characters
        cleanedValue = value.replace(/[^A-Za-z\s]/g, '');

        // Remove leading spaces
        cleanedValue = cleanedValue.replace(/^\s+/g, '');

        // Replace multiple spaces in between with a single space
        cleanedValue = cleanedValue.replace(/\s{2,}/g, ' ');

        // Update field if cleaned
        if (cleanedValue !== value) {
            $field.val(cleanedValue);
        }

        value = cleanedValue;

        // Required check
        if (isRequired && !value) {
            isValid = false;
            errorMsg = $field.attr('data-missing-error');
        }

        // Pattern: letters with optional single space in between
        var pattern = /^[A-Za-z]+(?: [A-Za-z]+)*$/;
        if (isValid && value && !pattern.test(value)) {
            isValid = false;
            errorMsg = $field.attr('data-invalid-error');
        }
    } else if (isEmailField) {
        value = $field.val() ? $field.val().trim() : '';

        if (isRequired && !value) {
            isValid = false;
            errorMsg = $field.attr('data-missing-error');
        } else if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            isValid = false;
            errorMsg = $field.attr('data-invalid-error');
        }
    } else if (isPhoneField) {
        value = $field.val() || '';
        maxlength = parseInt($field.attr('maxlength'), 10) || 10;

        // Digits-only enforcement
        var cleanedPhone = value.replace(/[^0-9]/g, '');

        // India-specific rule: first digit 6-9 if country code is +91
        var countryCode = $field.attr('data-dialing-code') || '';
        if (countryCode === '+91' && cleanedPhone.length > 0) {
            if (!/^[6-9]/.test(cleanedPhone)) {
                cleanedPhone = ''; // clear if invalid first digit
            }
        }

        // Update field if cleaned
        if (cleanedPhone !== value) $field.val(cleanedPhone);
        value = cleanedPhone;

        // Required / length checks
        if (isRequired && !value) {
            isValid = false;
            errorMsg = $field.attr('data-missing-error');
        } else if (value && value.length < maxlength) {
            isValid = false;
            errorMsg = $field.attr('data-invalid-error');
        }
    } else if (isPinCodeField) {
        value = $field.val() || '';

        // Allow only digits
        cleanedValue = value.replace(/[^0-9]/g, '');

        // Restrict to max 6 digits (remove extra digits only)
        if (cleanedValue.length > 6) {
            cleanedValue = cleanedValue.slice(0, 6);
        }

        // Update field if modified
        if (cleanedValue !== value) {
            $field.val(cleanedValue);
        }

        value = cleanedValue;

        var $cityField = $('#loyaltyAddressCity');
        var $stateField = $('#loyaltyAddressState');

        // If less than 6 digits → clear city/state
        if (value.length < 6) {
            $cityField.val('');
            $stateField.val('');
        }

        // Required check
        if (isRequired && !value) {
            isValid = false;
            errorMsg = $field.attr('data-missing-error');
        } else if (value && value.length !== 6) {
            isValid = false;
            errorMsg = $field.attr('data-invalid-error');
        } else if (value.length === 6) {
            clearTimeout(pincodeTimer);
            pincodeTimer = setTimeout(function () {
                fetchPlace(value);
            }, 400);
        }
    } else {
        value = $field.val() ? $field.val().trim() : '';
        if (isRequired && !value) {
            isValid = false;
            errorMsg = $field.attr('data-missing-error');
        }
    }

    // -----------------------
    // Show or clear errors
    // -----------------------
    if (isRequired) {
        if (!isValid) {
            $formGroup.addClass('is-invalid');
            $errorDiv.text(errorMsg || 'This field is required');
        } else {
            $formGroup.removeClass('is-invalid');
            $errorDiv.text('');
        }
    } else if (isOptionalLoyaltyField && value && value.length < maxlength) {
        $formGroup.addClass('is-invalid');
        $errorDiv.text(errorMsg);
    } else {
        $formGroup.removeClass('is-invalid');
        $errorDiv.text('');
    }

    if (isOptionalFaxField) {
        if (value && value.length > 0) {
            var faxFieldminlength = parseInt($field.attr('minlength'), 10) || 0;
            var faxFieldmaxlength = parseInt($field.attr('maxlength'), 10) || 10;

            if (value.length < faxFieldminlength || value.length > faxFieldmaxlength) {
                $formGroup.addClass('is-invalid');
                $errorDiv.text(errorMsg);
            } else {
                $formGroup.removeClass('is-invalid');
                $errorDiv.text('');
                isValid = true;
            }
        }
    }

    return isValid;
}

/**
 * Validates all inputs/selects/textareas within a given stage by delegating
 * field-level validation to `validateField()`.
 *
 * Special case:
 * - For stage 1, the `.partner-details-section` wrapper is skipped unless the
 *   `maritalStatus` input value is `married`.
 *
 * @param {JQuery} $stage - jQuery element for the current stage container.
 * @returns {boolean} True when every validated field in the stage is valid.
 */
function validateStage($stage) {
    var allValid = true;

    // Determine current stage number
    var stageNumber = parseInt($stage.attr('data-stage'), 10);

    // Loop through all form-group-wrapper-containers
    $stage.find('.form-group-wrapper-container').each(function () {
        var $wrapper = $(this);

        // Special case for stage 1
        if (stageNumber === 1 && $wrapper.hasClass('partner-details-section')) {
            var maritalStatus = $stage.find('input[name="maritalStatus"]').val();
            if (!maritalStatus || maritalStatus.toLowerCase() !== '02') {
                // Skip validation for partner-details-section if not married
                return true; // continue to next wrapper
            }
        }

        // Validate each input, select, textarea inside this wrapper
        $wrapper.find('input, select, textarea').each(function () {
            if (!validateField($(this))) {
                allValid = false;
            }
        });

        return true;
    });

    return allValid;
}

/**
 * Switches visible stage content.
 * Also scrolls the viewport to the top of the form wrapper.
 *
 * @param {number} currentStage - Current stage number (unused but kept for signature clarity).
 * @param {number} nextStage - Next stage number to activate.
 * @returns {void}
 */
function goToStage(currentStage, nextStage) {
    $('.form-stage').removeClass('active');
    $('.form-stage[data-stage="' + nextStage + '"]').addClass('active');

    // Scroll to top of the form wrapper, accounting for fixed header
    var $container = $('.form-wrapper-container');
    var headerHeight = $('header .header-wrapper').outerHeight() || 0; // adjust selector to your header
    if ($container.length) {
        $('html, body').animate({
            scrollTop: $container.offset().top - headerHeight - 10 // extra 10px spacing
        }, 400);
    }
}

/* ===============================
   CHILD FORM LOGIC (NEW)
================================ */

/**
 * Updates names/ids inside a child block so server-side parsing receives
 * indexed group fields (e.g., `unMarriedChildren[0][firstName]`).
 *
 * @param {JQuery} $block - jQuery element representing the child block wrapper.
 * @param {string} prefix - Field name prefix.
 * @param {number} index - 0-based block index.
 * @returns {void}
 */
function updateChildFieldNames($block, prefix, index) {
    // Title
    $block.find('.child-title-code')
        .attr('name', prefix + '[' + index + '][title]');

    // First name
    $block.find('.child-first-name')
        .attr({
            name: prefix + '[' + index + '][firstName]',
            id: prefix + '_' + index + '_firstName'
        });

    // Last name
    $block.find('.child-last-name')
        .attr({
            name: prefix + '[' + index + '][lastName]',
            id: prefix + '_' + index + '_lastName'
        });

    // Email
    $block.find('.child-email')
        .attr({
            name: prefix + '[' + index + '][email]',
            id: prefix + '_' + index + '_email'
        });

    // Mobile dial code
    $block.find('.child-mobile-dial-code')
        .attr('name', prefix + '[' + index + '][mobileDialCode]');

    // Mobile number
    $block.find('.child-mobile')
        .attr({
            name: prefix + '[' + index + '][mobile]',
            id: prefix + '_' + index + '_mobile'
        });

    // Date of Birth
    $block.find('.child-date-of-birth')
        .attr({
            name: prefix + '[' + index + '][dateOfBirth]',
            id: prefix + '_' + index + '_dateOfBirth'
        });

    if (prefix === 'marriedChildren') {
        // Address
        $block.find('.child-address')
            .attr({
                name: prefix + '[' + index + '][address]',
                id: prefix + '_' + index + '_address'
            });
    }
}

/**
 * Toggles the “Add Child” button based on maximum allowed blocks.
 *
 * @param {JQuery} $container - jQuery element for the group container.
 * @returns {void}
 */
function toggleAddButton($container) {
    var count = $container.find('.form-group-wrapper').length;

    if (count >= MAX_CHILDREN) {
        $container.find('.add-child-btn').addClass('d-none');
    } else {
        $container.find('.add-child-btn').removeClass('d-none');
    }
}

/**
 * Initializes a children section by normalizing already-rendered blocks
 * and ensuring the “Add” button visibility is correct.
 *
 * @param {string} containerSelector - Selector for the group container.
 * @param {string} namePrefix - Field name prefix (e.g., `unMarriedChildren`, `marriedChildren`).
 * @returns {void}
 */
function initChildrenSection(containerSelector, namePrefix) {
    var $container = $(containerSelector);

    // Normalize already-rendered blocks (first one)
    $container.find('.form-group-wrapper').each(function (index) {
        updateChildFieldNames($(this), namePrefix, index);
    });

    toggleAddButton($container);
}

/**
 * Requests and inserts a new child block into the given container.
 *
 * @param {string} containerSelector - Selector for the group container.
 * @param {string} prefix - Field name prefix.
 * @param {string} url - Endpoint that returns the HTML for one child block.
 * @returns {void}
 */
function addChild(containerSelector, prefix, url) {
    var $container = $(containerSelector);
    var count = $container.find('.form-group-wrapper').length;

    if (count >= MAX_CHILDREN) return;

    $.spinner().start();

    $.ajax({
        url: url, // your server endpoint returning the HTML
        type: 'GET',
        success: function (html) {
            $.spinner().stop();
            var $block = $(html);
            updateChildFieldNames($block, prefix, count);
            $container.find('.add-child-btn').before($block);
            toggleAddButton($container);
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

/**
 * Toggles the “Add Discontinuation” button based on maximum allowed blocks.
 *
 * @param {JQuery} $container - jQuery element for the group container.
 * @returns {void}
 */
function toggleDiscontinuationAddButton($container) {
    var count = $container.find('.form-group-wrapper').length;

    if (count >= MAX_DISCONTINUATIONS) {
        $container.find('.add-discontinuation-btn').addClass('d-none');
    } else {
        $container.find('.add-discontinuation-btn').removeClass('d-none');
    }
}

/**
 * Updates names/ids inside a discontinuation block so server-side parsing receives
 * indexed group fields (e.g., `discontinuations[0][membershipNumber]`).
 *
 * @param {JQuery} $block - jQuery element representing the discontinuation block wrapper.
 * @param {string} prefix - Field name prefix.
 * @param {number} index - 0-based block index.
 * @returns {void}
 */
function updateDiscontinuationFieldNames($block, prefix, index) {
    // Title
    $block.find('.discontinuation-title-code')
        .attr('name', prefix + '[' + index + '][title]');

    // First name
    $block.find('.discontinuation-first-name')
        .attr({
            name: prefix + '[' + index + '][firstName]',
            id: prefix + '_' + index + '_firstName'
        });

    // Last name
    $block.find('.discontinuation-last-name')
        .attr({
            name: prefix + '[' + index + '][lastName]',
            id: prefix + '_' + index + '_lastName'
        });

    // Membership number
    $block.find('.discontinuation-membership-number')
        .attr({
            name: prefix + '[' + index + '][membershipNumber]',
            id: prefix + '_' + index + '_membershipNumber'
        });
}

/**
 * Initializes a discontinuation section by normalizing already-rendered blocks
 * and ensuring the “Add” button visibility is correct.
 *
 * @param {string} containerSelector - Selector for the group container.
 * @param {string} prefix - Field name prefix (e.g., `discontinuations`).
 * @returns {void}
 */
function initDiscontinuationSection(containerSelector, prefix) {
    var $container = $(containerSelector);

    // Normalize already rendered blocks (first one)
    $container.find('.form-group-wrapper').each(function (index) {
        updateDiscontinuationFieldNames($(this), prefix, index);
    });

    toggleDiscontinuationAddButton($container);
}

/**
 * Requests and inserts a new discontinuation block into the given container.
 *
 * @param {string} containerSelector - Selector for the group container.
 * @param {string} prefix - Field name prefix.
 * @param {string} url - Endpoint that returns the HTML for one discontinuation block.
 * @returns {void}
 */
function addDiscontinuation(containerSelector, prefix, url) {
    var $container = $(containerSelector);
    var count = $container.find('.form-group-wrapper').length;

    if (count >= MAX_DISCONTINUATIONS) return;

    $.spinner().start();

    $.ajax({
        url: url, // returns ONE .form-group-wrapper
        type: 'GET',
        success: function (html) {
            $.spinner().stop();

            var $block = $(html);
            updateDiscontinuationFieldNames($block, prefix, count);

            $container.find('.add-discontinuation-btn').before($block);
            toggleDiscontinuationAddButton($container);
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

/**
 * Converts jQuery `serializeArray()` output into a nested JSON object.
 * Supports bracket notation like `group[0][field]`.
 *
 * @param {Array<{name: string, value: string}>} formArray - Result of `$form.serializeArray()`.
 * @returns {Object} Nested object representation of the form fields.
 */
function buildNestedJSON(formArray) {
    const json = {};

    formArray.forEach(field => {
        const keys = field.name.replace(/\]/g, '').split('[');
        let current = json;

        keys.forEach((key, index) => {
            const isLast = index === keys.length - 1;
            const nextKey = keys[index + 1];
            const isNextKeyArrayIndex = nextKey !== undefined && Number.isInteger(Number(nextKey));

            if (isLast) {
                if (current[key] !== undefined) {
                    if (Array.isArray(current[key])) {
                        current[key].push(field.value);
                    } else {
                        current[key] = [current[key], field.value];
                    }
                } else {
                    current[key] = field.value;
                }
            } else {
                if (current[key] === undefined) {
                    current[key] = isNextKeyArrayIndex ? [] : {};
                }
                current = current[key];
            }
        });
    });

    return json;
}

/**
 * Shows/hides the partner-details section based on the current marital status.
 *
 * - Reads `input[name="maritalStatus"]` and treats the value `married` (case-insensitive)
 *   as the condition to show `.form-container .partner-details-section`.
 * - Toggles the `d-none` class to hide/show the section.
 * - When hiding, clears validation UI state (`.is-invalid`) and error messages
 *   (`.invalid-feedback`) for fields inside the partner section.
 *
 * @returns {void}
 */
function togglePartnerDetailsSection() {
    var value = $('input[name="maritalStatus"]').val();
    var $partnerSection = $('.form-container .partner-details-section');

    // Show/hide partner details section
    var show = value && value.toLowerCase() === '02';
    $partnerSection.toggleClass('d-none', !show);

    // If hiding the section, clear only errors inside it
    if (!show) {
        $partnerSection.find('input, select, textarea').each(function () {
            var $field = $(this);
            var $formGroup = $field.closest('.form-group');
            var $errorDiv = $formGroup.find('.invalid-feedback');

            // Remove validation class from form-group
            $formGroup.removeClass('is-invalid');

            // Clear error messages
            $errorDiv.text('');
        });
    }
}

/**
 * Validates the “Terms & Conditions” agreement checkbox and updates the UI.
 *
 * - When unchecked: adds `is-invalid` to the closest `.form-check` wrapper and
 *   writes an error message into its `.invalid-feedback` element.
 * - When checked: removes `is-invalid` and clears the error message.
 *
 * @returns {boolean} `true` when the checkbox is checked; otherwise `false`.
 */
function validateTermsCheckbox() {
    var $checkbox = $('#agreeTermsConditions');
    var $wrapper = $checkbox.closest('.form-check');
    var $error = $wrapper.find('.invalid-feedback');
    var valid = true;

    if (!$checkbox.is(':checked')) {
        $wrapper.addClass('is-invalid');
        $error.text('Please accept terms and conditions');
        valid = false;
    } else {
        $wrapper.removeClass('is-invalid');
        $error.text('');
    }
    return valid;
}

/**
 * Sanitizes a single input field value in-place based on its CSS class.
 *
 * Supported rules:
 * - `.name-field`: strips digits, collapses multiple spaces, trims leading spaces.
 * - `.email-field`: strips characters outside `[A-Za-z0-9@._-]`.
 * - `.phone-field`: enforces digits-only, enforces max length via `data-maxlength` (default 10),
 *   and for India (`data-dialing-code="+91"`) clears the value if the first digit is not 6-9.
 *
 * Notes:
 * - This function does not show validation errors; it only normalizes user input.
 * - The field value may be modified via `$field.val()`.
 *
 * @param {JQuery} $field - The input element wrapped in jQuery.
 * @returns {void}
 */
function validateAllFields($field) {
    var value = $field.val();
    var cleanedValue;

    // -------------------
    // NAME FIELD
    // -------------------
    if ($field.hasClass('name-field')) {
        // Remove digits
        cleanedValue = value.replace(/\d/g, '');
        // Remove multiple spaces
        cleanedValue = cleanedValue.replace(/\s{2,}/g, ' ');
        // Remove leading spaces
        cleanedValue = cleanedValue.replace(/^\s+/g, '');
        // Update the field if changed
        if (cleanedValue !== value) $field.val(cleanedValue);
    } else if ($field.hasClass('email-field')) {
        // Remove invalid characters
        cleanedValue = value.replace(/[^A-Za-z0-9@._-]/g, '');
        if (cleanedValue !== value) $field.val(cleanedValue);
    } else if ($field.hasClass('phone-field')) {
        // Get current value
        value = $field.val() || '';

        // Get maxlength from data attribute or default 10
        var maxLength = parseInt($field.attr('data-maxlength'), 10) || 10;

        // Digits-only enforcement
        cleanedValue = value.replace(/\D/g, '');

        // India-specific rule: first digit 6-9 if dialing code is +91
        var countryCode = $field.attr('data-dialing-code') || '';
        if (countryCode === '+91' && cleanedValue.length > 0) {
            if (!/^[6-9]/.test(cleanedValue)) {
                cleanedValue = ''; // clear if first digit is invalid
            }
        }

        // Enforce maxlength
        if (cleanedValue.length > maxLength) {
            cleanedValue = cleanedValue.substring(0, maxLength);
        }

        // Update the field if the value changed
        if (cleanedValue !== value) {
            $field.val(cleanedValue);
        }
    }
}

/**
 * Fetches the Loyalty Enrolment form markup and injects it into the page.
 *
 * - Reads the endpoint from the `data-url` attribute of `#get-loyalty-enrolment-form`.
 * - Performs a GET request and, on success:
 *   - Redirects when `{ success: false }` is returned.
 *   - Otherwise replaces the container HTML with the returned markup.
 * - After injecting markup, re-initializes the dynamic repeatable sections
 *   (children groups and discontinuation requests) so existing blocks have
 *   correctly indexed names/ids and the “Add” buttons are toggled properly.
 *
 * @returns {void}
 */
function getLoyaltyEnrolmentForm() {
    var $form = $('#get-loyalty-enrolment-form');
    var $url;

    if ($form.length > 0) {
        $url = $form.attr('data-url');
    }

    if (!$url) {
        return;
    }

    $.spinner().start();

    $.ajax({
        url: $url,
        type: 'GET',
        success: function (res) {
            $.spinner().stop();
            if (res.success === false) {
                window.location.href = res.redirectUrl;
            } else {
                $('#get-loyalty-enrolment-form').html(res);
                /* ---- Children sections init ---- */
                initChildrenSection('#unmarriedChildrenContainer', 'unMarriedChildren');
                initChildrenSection('#marriedChildrenContainer', 'marriedChildren');
                initDiscontinuationSection('#discontinuation-requests-container', 'discontinuations');
            }
        },
        error: function () {
            $.spinner().stop();
        }
    });
}

$(document).on('input change blur', '.name-field, .email-field, .phone-field', function () {
    validateAllFields($(this));
});

/* ===============================
   DOCUMENT READY
================================ */
$(document).ready(function () {
    /* ---- Stage navigation ---- */

    getLoyaltyEnrolmentForm();
    togglePartnerDetailsSection();

    $('body').on('click', '.form-container .btn-next', function () {
        var $currentStage = $(this).closest('.form-stage');
        var currentStageNumber = parseInt($currentStage.attr('data-stage'), 10);
        var nextStageNumber = currentStageNumber + 1;

        if (validateStage($currentStage)) {
            goToStage(currentStageNumber, nextStageNumber);
            updateProgressBar(nextStageNumber);
        }
    });

    $('body').on('click', '.form-container .btn-back', function () {
        var $currentStage = $(this).closest('.form-stage');
        var currentStageNumber = parseInt($currentStage.attr('data-stage'), 10);
        var prevStageNumber = currentStageNumber - 1;

        goToStage(currentStageNumber, prevStageNumber);
        updateProgressBar(prevStageNumber);
    });

    $('body').on('submit', '#loyalty-enrolment-form', function (e) {
        e.preventDefault();

        var $form = $(this);
        var isValid = true;

        // -----------------------
        // Terms & Conditions check
        // -----------------------
        if (!validateTermsCheckbox()) {
            isValid = false;
        }

        // Stop submission if checkbox invalid
        if (!isValid) {
            return;
        }

        var formDataArray = $form.serializeArray();
        var formDataNested = buildNestedJSON(formDataArray);
        var url = $form.attr('action');

        $.spinner().start();

        $.ajax({
            url: url,
            type: 'POST',
            data: {
                form: JSON.stringify(formDataNested)
            },
            success: function (response) {
                if (response.success) {
                    $('#loyaltyFormContainer').addClass('d-none');
                    $('#loyaltySuccessContainer').removeClass('d-none');
                    window.scrollTo(0, 0);
                    $('#ckcstaticloyalityrewardenrolmentform').addClass('success-submission');
                }
                $.spinner().stop();
            },
            error: function () {
                $.spinner().stop();
            }
        });
    });

    /* ---- Add child button ---- */
    $('body').on('click', '.form-container .add-child-btn', function () {
        var $container = $(this).closest('.form-group-wrapper-container');
        var prefix = $container.attr('data-group'); // unmarriedChildren / marriedChildren
        var url = $(this).attr('data-action-url');

        addChild('#' + $container.attr('id'), prefix, url);
    });

    $('body').on('click', '.form-container .add-discontinuation-btn', function () {
        var $container = $(this).closest('.form-group-wrapper-container');
        var prefix = $container.attr('data-group'); // discontinuations
        var url = $(this).attr('data-action-url');

        addDiscontinuation('#' + $container.attr('id'), prefix, url);
    });

    $('body').on('click', '.form-container .custom-option-value', function () {
        var $option = $(this);
        var selectedValue = $option.attr('data-value');

        var $dropdownSection = $option.closest('.custom-drop-down-section');
        var $hiddenInput = $dropdownSection.find('.dropdown-selected-value');

        // Update hidden input value
        $hiddenInput.val(selectedValue).trigger('change');

        // Update title description hidden input if present (e.g. loyaltyPartnerTitle)
        var $titleDescInput = $dropdownSection.find('input[name="loyaltyPartnerTitle"]');
        if ($titleDescInput.length) {
            $titleDescInput.val(selectedValue);
        }

        // Update visible selected text
        $dropdownSection
            .find('.selected-value')
            .addClass('active')
            .text(selectedValue);
    });

    $('body').on('change', '.form-container input[name="maritalStatus"]', function () {
        togglePartnerDetailsSection();
    });

    $('body').on('input change blur', '.form-container .first-stage-container input, .form-container .first-stage-container select, .form-container .first-stage-container textarea', function () {
        validateField($(this));
    });

    $('body').on('click', '.form-container .form-group-wrapper .date-picker-wrapper', function (e) {
        e.preventDefault();
        // var $dateInput = $('.child-date-of-birth');
        var $this = $(this);

        var $dateInput = $this.closest('.form-group')
            .find('.child-date-of-birth, .partner-date-of-birth');

        if ($dateInput.length && !$dateInput.data('datepicker')) { // initialize once
            $.fn.datepicker.dates.en.daysMin = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

            $dateInput.datepicker({
                format: 'dd/mm/yyyy',
                language: 'en',
                autoclose: true,
                todayHighlight: false,
                endDate: new Date(),
                weekStart: 1,
                container: $this,
                maxViewMode: 2
            });

            $dateInput.on('show', function () {
                $('.table-condensed .prev').text('');
                $('.table-condensed .next').text('');
            });
        }

        $dateInput.datepicker('show');
    });

    $('body').on('click', '.reset-terms-and-condition', function (e) {
        e.preventDefault();
        if (!$('#agreeTermsConditions').is(':checked')) {
            $('#loyalty-terms-modal').modal('show');
            $('#loyalty-terms-modal').addClass('reset-terms-visible');
            $('.modal-backdrop').last().addClass('reset-terms-backdrop');
        } else {
            $('#agreeTermsConditions').prop('checked', false);
        }
    });

    $('body').on('click', '#reset-agree-btn', function () {
        $('#agreeTermsConditions').prop('checked', true);
        $('#loyalty-terms-modal').modal('hide');
        validateTermsCheckbox();
        // $('.register-password-check-box').addClass('d-none');
    });

    $('body').on('click', '.reset-close', function () {
        $('#agreeTermsConditions').prop('checked', false);
        $('#loyalty-terms-modal').modal('hide');
        validateTermsCheckbox();
    });

    $('body').on('click', '.dialing-code-option', function () {
        var $clickedOption = $(this);

        // Define variables for the attributes
        var phonelength = parseInt($clickedOption.attr('data-phonelength'), 10) || 10;
        var dialingCode = $clickedOption.attr('data-value') || '';

        // Find the parent wrapper
        var $wrapper = $clickedOption.closest('.mobile-dial-code-phone-wrapper');

        // Find the input field inside the wrapper
        var $inputField = $wrapper.find('.form-control');

        // Get previously selected dialing code
        var previousDialingCode = $inputField.attr('data-dialing-code') || '';

        // Only clear input if the country changed
        if (previousDialingCode !== dialingCode) {
            $inputField.val(''); // clear phone field
        }

        // Set attributes using the variables
        $inputField.attr('maxlength', phonelength);
        $inputField.attr('data-dialing-code', dialingCode);
    });

    $('body').on('click', '#loyaltyprogram .hero-button', function (e) {
        e.preventDefault();

        var redirecturl = $('#check-loyalty-enrolment-status').attr('data-url');
        var loyaltyPageUrl = $('#check-loyalty-enrolment-status').attr('data-loyalty-page-url');

        if ($('#isAuthenticated').val() !== 'true') {
            if (loyaltyPageUrl) {
                localStorage.setItem('getLoyaltyEnrolmentStatus', loyaltyPageUrl);
            }
            $('#ckc-login-modal').modal('show');
            return;
        }

        $.spinner().start();
        $.ajax({
            url: redirecturl,
            type: 'GET',
            success: function (response) {
                $.spinner().stop();

                if (response.success && response.isEnrolled) {
                    $('#already-enrolled-modal').modal('show');
                } else if (response.success && !response.isEnrolled) {
                    window.location.href = response.redirectUrl;
                } else {
                    toastMessageHelper.showNotification(response.message);
                    window.location.href = response.redirectUrl;
                }
            },
            error: function () {
                $.spinner().stop();
            }
        });
    });
});

$(window).on('load', function () {
    var params = new URLSearchParams(window.location.search);
    if (!params.get('loyaltyLogin')) {
        return;
    }

    var $alreadyEnrolledModal = $('#already-enrolled-modal');
    if ($alreadyEnrolledModal.length) {
        $alreadyEnrolledModal.modal('show');
    }

    params.delete('loyaltyLogin');
    var newUrl = window.location.pathname
        + (params.toString() ? '?' + params.toString() : '')
        + window.location.hash;
    window.history.replaceState({}, document.title, newUrl);
});
